import React, { useState, useEffect } from 'react';
import { getApiBaseUrl } from '@/config/environment';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Alert,
  Divider,
  Paper,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  TextField,
  useTheme,
  alpha,
} from '@mui/material';
import {
  ToggleOn as ToggleIcon,
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
  PersonOutline,
  SettingsOutlined,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';

// API_BASE is now dynamic

interface FeatureSetting {
  enabled: boolean;
  user_override_allowed?: boolean;
  description: string;
  updatedAt: string;
  page_size?: number;
  max_page_size?: number;
}

interface AppFeatures {
  show_host_own_vehicles: FeatureSetting;
  infinite_scroll: FeatureSetting;
}

export default function FeatureTogglesPage() {
  const theme = useTheme();
  const [features, setFeatures] = useState<AppFeatures | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState<string | null>(null);

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/settings/app-features`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        setFeatures(data.data);
      } else {
        throw new Error(data.error || 'Failed to load features');
      }
    } catch (error) {
      console.error('❌ Failed to load feature settings:', error);
      toast.error('Failed to load feature settings');
    } finally {
      setLoading(false);
    }
  };

  const updateFeature = async (settingKey: string, settingValue: any) => {
    setUpdating(settingKey);
    try {
      // Get admin user ID from localStorage
      const getAdminUserId = (): string => {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          try {
            const user = JSON.parse(userStr);
            return user.id || user.userId || user.admin_id || 'admin-default';
          } catch (e) {
          }
        }
        return 'admin-default';
      };

      const response = await fetch(`${getApiBaseUrl()}/api/v1/admin/settings/app-features`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          settingKey,
          settingValue,
          adminUserId: getAdminUserId()
        })
      });

      const data = await response.json();
      if (data.success) {
        toast.success('Feature setting updated successfully');
        await fetchFeatures(); // Refresh
      } else {
        throw new Error(data.error || 'Failed to update setting');
      }
    } catch (error) {
      console.error('❌ Failed to update feature setting:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to update setting');
    } finally {
      setUpdating(null);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
          <ToggleIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Typography variant="h4" fontWeight="bold">
            App Feature Toggles
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Control mobile app features globally without rebuilding or redeploying
        </Typography>
      </Box>

      {/* Show Host Own Vehicles Card */}
      <Card sx={{ mb: 3, boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Visibility sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              Show Host Own Vehicles
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {features?.show_host_own_vehicles?.description || 'Whether hosts can see their own vehicles in renter dashboard'}
          </Typography>

          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mb: 3 }}>
            Last updated: {features?.show_host_own_vehicles?.updatedAt
              ? new Date(features.show_host_own_vehicles.updatedAt).toLocaleString()
              : 'Never'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Feature Enabled Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={features?.show_host_own_vehicles?.enabled || false}
                onChange={(e) => {
                  updateFeature('show_host_own_vehicles', {
                    enabled: e.target.checked,
                    user_override_allowed: features?.show_host_own_vehicles?.user_override_allowed
                  });
                }}
                disabled={updating === 'show_host_own_vehicles'}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Feature Enabled
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Globally enable or disable this feature for all users
                </Typography>
              </Box>
            }
            sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
          />

          {/* User Override Toggle (only show if feature is enabled) */}
          {features?.show_host_own_vehicles?.enabled && (
            <FormControlLabel
              control={
                <Switch
                  checked={features?.show_host_own_vehicles?.user_override_allowed || false}
                  onChange={(e) => {
                    updateFeature('show_host_own_vehicles', {
                      enabled: features?.show_host_own_vehicles?.enabled,
                      user_override_allowed: e.target.checked
                    });
                  }}
                  disabled={updating === 'show_host_own_vehicles'}
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Allow User Override
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    When enabled, hosts can choose in their settings whether to see their own vehicles
                  </Typography>
                </Box>
              }
              sx={{ mb: 2, display: 'flex', alignItems: 'flex-start' }}
            />
          )}

          {/* Current Behavior Display */}
          <Paper
            sx={{
              mt: 3,
              p: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Current Behavior:
            </Typography>
            <List dense>
              {!features?.show_host_own_vehicles?.enabled ? (
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Cancel color="error" fontSize="small" />
                        <Typography variant="body2">
                          All hosts' own vehicles are HIDDEN from renter view
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ) : features?.show_host_own_vehicles?.user_override_allowed ? (
                <>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <CheckCircle color="success" fontSize="small" />
                          <Typography variant="body2">
                            Hosts can see their own vehicles by default
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonOutline color="primary" fontSize="small" />
                          <Typography variant="body2">
                            Hosts can toggle this in their personal settings
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                </>
              ) : (
                <ListItem>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle color="success" fontSize="small" />
                        <Typography variant="body2">
                          All hosts ALWAYS see their own vehicles
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </CardContent>
      </Card>

      {/* Infinite Scroll Settings Card */}
      <Card sx={{ boxShadow: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <SettingsOutlined sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h6" fontWeight="bold">
              Infinite Scroll Settings
            </Typography>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {features?.infinite_scroll?.description || 'Infinite scroll pagination settings for vehicle listings'}
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Infinite Scroll Enabled Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={features?.infinite_scroll?.enabled || false}
                onChange={(e) => {
                  updateFeature('infinite_scroll', {
                    enabled: e.target.checked,
                    page_size: (features?.infinite_scroll as any)?.page_size || 20,
                    max_page_size: (features?.infinite_scroll as any)?.max_page_size || 50
                  });
                }}
                disabled={updating === 'infinite_scroll'}
              />
            }
            label={
              <Box>
                <Typography variant="body1" fontWeight="medium">
                  Infinite Scroll Enabled
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Enable pagination for vehicle listings
                </Typography>
              </Box>
            }
            sx={{ mb: 3, display: 'flex', alignItems: 'flex-start' }}
          />

          {/* Page Size Settings */}
          {features?.infinite_scroll?.enabled && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                Page Size (vehicles per load)
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  type="number"
                  size="small"
                  value={(features?.infinite_scroll as any)?.page_size || 20}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    if (newValue >= 1 && newValue <= 100) {
                      updateFeature('infinite_scroll', {
                        enabled: features?.infinite_scroll?.enabled,
                        page_size: newValue,
                        max_page_size: (features?.infinite_scroll as any)?.max_page_size || 50
                      });
                    }
                  }}
                  inputProps={{ min: 1, max: 100, step: 5 }}
                  sx={{ width: 120 }}
                  disabled={updating === 'infinite_scroll'}
                />
                <Typography variant="caption" color="text.secondary">
                  vehicles (1-100)
                </Typography>
              </Box>
            </Box>
          )}

          {/* Max Page Size Settings */}
          {features?.infinite_scroll?.enabled && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="body2" fontWeight="medium" sx={{ mb: 1 }}>
                Maximum Page Size
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  type="number"
                  size="small"
                  value={(features?.infinite_scroll as any)?.max_page_size || 50}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    if (newValue >= 10 && newValue <= 200) {
                      updateFeature('infinite_scroll', {
                        enabled: features?.infinite_scroll?.enabled,
                        page_size: (features?.infinite_scroll as any)?.page_size || 20,
                        max_page_size: newValue
                      });
                    }
                  }}
                  inputProps={{ min: 10, max: 200, step: 10 }}
                  sx={{ width: 120 }}
                  disabled={updating === 'infinite_scroll'}
                />
                <Typography variant="caption" color="text.secondary">
                  vehicles (10-200)
                </Typography>
              </Box>
            </Box>
          )}

          {/* Current Settings Summary */}
          <Paper
            sx={{
              mt: 3,
              p: 2,
              bgcolor: alpha(theme.palette.info.main, 0.05),
              border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
              Current Behavior:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText
                  primary={
                    <Typography variant="body2">
                      {features?.infinite_scroll?.enabled ? (
                        <>
                          ✅ Loading <strong>{(features?.infinite_scroll as any)?.page_size || 20} vehicles</strong> per page
                        </>
                      ) : (
                        '❌ Infinite scroll disabled - loading all vehicles at once'
                      )}
                    </Typography>
                  }
                />
              </ListItem>
              {features?.infinite_scroll?.enabled && (
                <ListItem>
                  <ListItemText
                    primary={
                      <Typography variant="body2">
                        📊 Maximum allowed: <strong>{(features?.infinite_scroll as any)?.max_page_size || 50} vehicles</strong> per request
                      </Typography>
                    }
                  />
                </ListItem>
              )}
            </List>
          </Paper>
        </CardContent>
      </Card>

      {/* Usage Scenarios Info */}
      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          Common Usage Scenarios:
        </Typography>
        <Typography variant="body2" component="div">
          <strong>1. Hide All Hosts' Own Vehicles:</strong> During peak traffic, toggle "Feature Enabled" OFF
          <br />
          <strong>2. Let Each Host Decide:</strong> Normal operation - turn both toggles ON
          <br />
          <strong>3. Force Show All:</strong> Launch phase - "Feature Enabled" ON, "Allow User Override" OFF
        </Typography>
      </Alert>
    </Box>
  );
}
